import { useEffect } from 'react';
import { Link } from 'react-router';
import { APP_NAME } from '@sellit/shared';
import { PoweredBy, Wordmark } from '../lib/Brand';
import { setAppTitle } from '../lib/format';
import { Icon } from '../lib/Icon';
import { PolicySection as Section } from '../lib/Policy';

const CONTACT = 'sellit@10digits.net';
/** Shown date of TERMS_VERSION. Change both together, and only for substantive changes. */
const EFFECTIVE = 'October 25, 2026';

// Draft Terms of Service, written for review by legal counsel before public launch. Keep in step
// with what VenLink actually does (it lists and connects; it doesn't pay, ship, verify or guarantee).
export function TermsPage() {
  useEffect(() => setAppTitle('Terms of Service'), []);
  const mail = <a href={`mailto:${CONTACT}`}>{CONTACT}</a>;
  return (
    <>
      <main className="page policy">
        <nav className="policy-top">
          <Link to="/" className="policy-brand" aria-label={`${APP_NAME} home`}><Wordmark className="wordmark" /></Link>
          <Link to="/" className="link"><Icon name="back" size={18} /> Back to {APP_NAME}</Link>
        </nav>

        <header className="policy-head">
          <h1>Terms of Service</h1>
          <p className="policy-tagline">The rules for selling and buying on {APP_NAME}.</p>
          <p className="muted small">Operated by 10 Digits LLC · Effective {EFFECTIVE}</p>
        </header>

        <section className="policy-glance" aria-labelledby="glance">
          <h2 id="glance">The short version</h2>
          <ul>
            <li>
              <span className="feature-icon"><Icon name="link" size={20} /></span>
              <span><strong>We list and connect</strong><span className="muted">{APP_NAME} shows your items and passes buyers' messages to you. The sale itself is between buyer and seller.</span></span>
            </li>
            <li>
              <span className="feature-icon"><Icon name="info" size={20} /></span>
              <span><strong>No payments, shipping or guarantees</strong><span className="muted">We don't handle money or delivery, check identities, or promise that anyone is trustworthy or that items sell.</span></span>
            </li>
            <li>
              <span className="feature-icon"><Icon name="shield" size={20} /></span>
              <span><strong>Be honest and stay safe</strong><span className="muted">List only what you own and may sell, describe it truthfully, and meet in safe places.</span></span>
            </li>
          </ul>
        </section>

        <Section n={1} title="Acceptance of these Terms">
          <p>
            These Terms are an agreement between you and 10 Digits LLC ("we", "us"), which operates {APP_NAME}. Sellers
            must accept them before using seller features. Buyers accept them by sending an inquiry or offer. If you don't
            agree, please don't use {APP_NAME}.
          </p>
        </Section>

        <Section n={2} title="Eligibility and accounts">
          <ul>
            <li>You must be at least 18 years old, or the age of majority where you live, to create a seller account or send an inquiry.</li>
            <li>Seller accounts are created by invitation. Keep your password private. You are responsible for activity under your account.</li>
            <li>Give accurate account information, and tell us at {mail} if you think someone else has used your account.</li>
          </ul>
        </Section>

        <Section n={3} title="Sellers: your rights and obligations">
          <ul>
            <li>You keep ownership of your photos and descriptions. You give us a non-exclusive, worldwide, royalty-free license to host, display, resize and share them to run {APP_NAME} and your sale pages, until you delete them.</li>
            <li>You are responsible for your listings, for answering buyers, and for completing or cancelling each sale.</li>
            <li>You are responsible for any taxes, permits or local rules (for example, garage sale permits) that apply to your sale.</li>
            <li>Don't put your street address, other people's personal information, or anything you don't want public in photos or descriptions.</li>
          </ul>
        </Section>

        <Section n={4} title="Buyers: your responsibilities">
          <ul>
            <li>Give a real name and a phone number you can be reached on, and contact sellers only about their items.</li>
            <li>Check items yourself before you pay. Make only offers you intend to honor.</li>
            <li>Don't use {APP_NAME} to spam, harass or mislead sellers.</li>
          </ul>
        </Section>

        <Section n={5} title="Accurate listings and lawful ownership">
          <p>
            Sellers may list only items they own, or have the right to sell, and that are lawful to sell where the sale takes
            place. Descriptions, condition, prices and photos must be accurate and must show the actual item.
          </p>
        </Section>

        <Section n={6} title="Prohibited items and fraud">
          <p>You may not list or use {APP_NAME} for:</p>
          <ul>
            <li>Weapons, ammunition, explosives, drugs, drug paraphernalia, alcohol, tobacco or vaping products, or prescription medicines.</li>
            <li>Stolen, counterfeit or recalled goods, or items that infringe someone else's rights.</li>
            <li>Animals, human remains or body parts, hazardous materials, or anything illegal to sell where you are.</li>
            <li>Adult content, services, tickets or listings that are not physical items for sale.</li>
            <li>Scams, fake listings, phishing, requests for advance payment or gift cards, or any fraud.</li>
          </ul>
        </Section>

        <Section n={7} title="No identity or background checks">
          <p>
            We don't routinely verify the identity of sellers or buyers, and we don't run background checks. Use your own
            judgment about who you deal with.
          </p>
        </Section>

        <Section n={8} title="No guarantee of trustworthiness">
          <p>
            We don't guarantee that any seller or buyer is honest, reliable or who they say they are, or that a listing is
            accurate. Report anything suspicious to {mail}.
          </p>
        </Section>

        <Section n={9} title="No payment processing">
          <p>
            {APP_NAME} doesn't process, hold or transfer payments. Buyers and sellers agree on payment between themselves.
            We never ask you to pay through {APP_NAME}, and we don't charge sellers fees or commissions today.
          </p>
        </Section>

        <Section n={10} title="No escrow or financial protection">
          <p>
            We don't offer escrow, refunds, chargebacks, purchase protection or insurance. Any refund or return is between
            the buyer and the seller.
          </p>
        </Section>

        <Section n={11} title="No shipping or delivery">
          <p>{APP_NAME} doesn't ship, deliver, store or move items. Pickup and delivery are arranged by the buyer and seller.</p>
        </Section>

        <Section n={12} title="We don't supervise pickups or exchanges">
          <p>
            We aren't present at, and don't supervise, inspect or approve, any meeting, pickup, inspection or exchange of
            items or money.
          </p>
        </Section>

        <Section n={13} title="Your personal safety">
          <ul>
            <li>Meet in a well-lit public place or have someone with you when you can. Tell someone where you're going.</li>
            <li>Sellers: think about what a buyer can see at your home, and keep valuables out of sight.</li>
            <li>Never share bank logins or verification codes, and be wary of anyone who asks to pay by gift card or wire transfer, or to overpay.</li>
            <li>If you feel unsafe, leave and contact local authorities.</li>
          </ul>
        </Section>

        <Section n={14} title="No guarantee that items will sell">
          <p>We don't promise any number of views, inquiries, offers or sales, or any sale price.</p>
        </Section>

        <Section n={15} title="Disputes between buyers and sellers">
          <p>
            Buyers and sellers are responsible for resolving disputes between themselves. We may, but don't have to, help.
            To the extent the law allows, you release 10 Digits LLC from claims that arise from your dealings with other
            users.
          </p>
        </Section>

        <Section n={16} title="Disclaimer of warranties">
          <p>
            {APP_NAME} is provided "as is" and "as available". To the extent the law allows, we disclaim all warranties,
            express or implied, including merchantability, fitness for a particular purpose and non-infringement. We don't
            promise that {APP_NAME} will always be available, error-free or secure.
          </p>
        </Section>

        <Section n={17} title="Limitation of liability">
          <p>
            To the extent the law allows, 10 Digits LLC isn't liable for indirect, incidental, special, consequential or
            punitive damages, or for lost profits, data or goodwill, arising from your use of {APP_NAME} or your dealings with
            other users. Our total liability for any claim relating to {APP_NAME} is limited to $100. Some places don't allow
            these limits, so they may not all apply to you. Nothing here limits liability that can't be limited by law.
          </p>
        </Section>

        <Section n={18} title="Suspension and removal">
          <p>
            We may remove or hide a listing, or suspend or close an account, if we reasonably believe it breaks these Terms,
            puts people at risk, or is required by law. Where reasonable, we'll tell you why. You can stop using {APP_NAME}
            and ask us to delete your account at any time.
          </p>
        </Section>

        <Section n={19} title="Privacy and personal information">
          <p>
            Our <Link to="/privacy">Privacy Policy</Link> explains what we collect and how we use it. Use other users'
            contact details only to arrange the sale they relate to.
          </p>
        </Section>

        <Section n={20} title="Changes to these Terms">
          <p>
            We may update these Terms. For a significant change, we'll update the effective date and ask sellers to accept
            the new version before they continue. Changes don't apply to sales already arranged.
          </p>
        </Section>

        <Section n={21} title="Contact, governing law and other terms">
          <ul>
            <li>{APP_NAME} is operated by 10 Digits LLC. Questions about these Terms: {mail}.</li>
            <li>These Terms are governed by the laws of the State of Texas, without regard to conflict-of-law rules, except where the law where you live says otherwise.</li>
            <li>If part of these Terms can't be enforced, the rest still applies. Not enforcing a term isn't a waiver of it.</li>
          </ul>
        </Section>
      </main>
      <PoweredBy showLogin />
    </>
  );
}
